<script setup lang="ts">
import NavBar from "@/components/shared/navbar/Navbar.vue";
import {
  createAdminSettingsCategories,
  visibleSettingsSubmenu,
} from "@/components/shared/settings/settingsSections";
import { useInvitePermissions } from "@/composables/models/invite/useInvitePermissions";
import VersionChecker from "@/components/shared/VersionChecker.vue";
import { usePageTitle } from "@/composables/shared/usePageTitle";
import type { SettingsMenuItem, SettingsSubMenuItem } from "@/types/settings";
import type { IAdminPageProps } from "@/utils/adminInterfaces";
import { computed, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

const { updateTitleFromPaths } = usePageTitle();
const route = useRoute();
const router = useRouter();

const routeCategory = (): string | undefined => (route.params as { category?: string })?.category;
const routeSection = (): string | undefined => (route.params as { section?: string })?.section;

const { t } = useI18n();

const { canAccessInviteRolesSettings } = useInvitePermissions();

const categories = computed((): SettingsMenuItem[] =>
  createAdminSettingsCategories().map((category) => {
    if (category.key !== "users" || !category.sections) {
      return category;
    }

    return {
      ...category,
      sections: category.sections.map((section) =>
        section.key === "invite-roles"
          ? { ...section, hidden: !canAccessInviteRolesSettings.value }
          : section
      ),
    };
  })
);

const activeCategory = computed((): SettingsMenuItem => {
  const param = routeCategory();
  const foundCategory = categories.value.find((item) => item.key === param && !item.disabled);
  return (
    foundCategory || (categories.value.find((item) => item.key === "general") as SettingsMenuItem)
  );
});

const activeSection = computed((): SettingsSubMenuItem => {
  const param = routeSection();
  const availableSections = activeCategory.value.sections || [];
  const foundSection = availableSections.find((item) => item.key === param && !item.disabled);
  return (
    foundSection ||
    (availableSections.find((item) => !item.disabled && !item.hidden) as SettingsSubMenuItem)
  );
});

watch(
  () => route.path,
  async () => {
    await nextTick();
    updateTitleFromPaths(
      [
        activeSection.value.label ? t(activeSection.value.label) : "",
        t(activeCategory.value.label),
        t("generic.entity.adminSettings"),
      ].filter(Boolean)
    );
  },
  { immediate: true, deep: true }
);

const handleCategorySelect = (key: SettingsMenuItem["key"], item: SettingsMenuItem) => {
  if (item.disabled) return;
  const list = visibleSettingsSubmenu(item.sections);
  const firstEnabled = list.find((s) => !s.disabled);
  if (firstEnabled) router.push(`/admin-settings/${key}/${firstEnabled.key}`);
  else router.push(`/admin-settings/${key}`);
};

const handleSectionSelect = (key: SettingsSubMenuItem["key"], item: SettingsSubMenuItem) => {
  if (item.disabled) return;
  router.push(`/admin-settings/${activeCategory.value.key}/${key}`);
};

const handleDirectSelect = (
  key: SettingsSubMenuItem["key"],
  item: SettingsSubMenuItem,
  parent: SettingsMenuItem
) => {
  if (item.disabled) return;
  router.push(`/admin-settings/${parent.key}/${key}`);
};

watch(
  [activeCategory, activeSection],
  ([cat, sec]) => {
    const expected = sec.key
      ? `/admin-settings/${cat.key}/${sec.key}`
      : `/admin-settings/${cat.key}`;
    if (route.path !== expected) router.replace(expected);
  },
  { immediate: true }
);
</script>

<template>
  <v-layout>
    <NavBar
      is-admin-page
      v-bind="
        {
          categories,
          activeCategory,
          activeSection,
          handleCategorySelect,
          handleSectionSelect,
          handleDirectSelect,
        } satisfies IAdminPageProps
      "
    />
    <v-main
      class="tw:!flex tw:flex-col tw:h-dvh tw:!pb-0 tw:bg-on-background tw:dark:bg-surface-muted"
    >
      <v-container class="tw:!max-w-full tw:h-full tw:grow-1 tw:!p-0 tw:md:!pt-1 tw:md:!pl-2">
        <VersionChecker />
        <router-view v-slot="{ Component }">
          <component
            :is="Component"
            v-bind="
              {
                categories,
                activeCategory,
                activeSection,
                handleCategorySelect,
                handleSectionSelect,
                handleDirectSelect,
              } satisfies IAdminPageProps
            "
          />
        </router-view>
      </v-container>
    </v-main>
  </v-layout>
</template>

<style scoped></style>
